import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';

import { EventType } from '@azure/msal-browser';
import { MsalBroadcastService } from '@azure/msal-angular';
import { BehaviorSubject, filter, Observable, of, switchMap, tap } from 'rxjs';

import { Store } from '@ngxs/store';
import { UserState } from '@gea/digital-ui-lib';
import { BASE_URL, SUBSCRIPTION_KEY } from '@shared/utils/tokens';

interface HttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  context?: HttpContext;
  observe?: 'body';
  params?: HttpParams | { [param: string]: string | number | boolean | Readonly<string | number | boolean>[] };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

interface BlobHttpOptions extends HttpOptions {
  responseType?: 'json';
}

const CAUGHT_EVENTS: EventType[] = [
  EventType.ACQUIRE_TOKEN_START,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.ACQUIRE_TOKEN_FAILURE,
];

/**
 * use this service instead of the httpClient to make sure
 * some basic headers are already taken care of.
 */
@Injectable({
  providedIn: 'root',
})
export class BRCApiService {
  private readonly _idToken = new BehaviorSubject<string>('');
  private readonly _afterRefreshed = new BehaviorSubject(false);
  private readonly _expirationTime = new BehaviorSubject(0);

  protected readonly msalBroadcast = inject(MsalBroadcastService);
  protected readonly http = inject(HttpClient);
  protected readonly store = inject(Store);

  constructor(
    @Inject(BASE_URL) protected apiUrl: string,

    @Inject(SUBSCRIPTION_KEY) protected subscriptionKey: string | undefined
  ) {
    // always get the latest token
    this.store
      .select(UserState.token)
      .pipe(filter((token) => token !== ''))
      .subscribe((idToken: string) => this._idToken.next(idToken));

    // while the token is being refreshed, allow deferring any calls
    this.msalBroadcast.msalSubject$
      .pipe(
        filter(({ eventType }) => CAUGHT_EVENTS.includes(eventType)),
        tap(({ eventType }) => {
          this._afterRefreshed.next(eventType === EventType.ACQUIRE_TOKEN_START);
        })
      )
      .subscribe();

    // get expiration time to avoid edge cases where fetching the new token starts just as a request is being made
    this.store.select(UserState.expirationTime).subscribe((time) => {
      this._expirationTime.next(time ?? 0);
    });
  }

  private mergeHeaders(
    base: HttpHeaders | Record<string, string | string[]> | undefined,
    extra: Record<string, string>
  ): HttpHeaders | Record<string, string | string[]> {
    if (base instanceof HttpHeaders) {
      let headers = base;
      Object.entries(extra).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
      return headers;
    }
    return { ...extra, ...(base || {}) };
  }

  get<T>(url: string, options: HttpOptions = {}, apiVersion?: string): Observable<T> {
    return this.send(url, options, apiVersion, {}, (fullUrl, requestOptions) => this.http.get<T>(fullUrl, requestOptions));
  }

  getBlob<T>(url: string, options: BlobHttpOptions, apiVersion?: string): Observable<T> {
    return this.send(
      url,
      options,
      apiVersion,
      {},
      (fullUrl, requestOptions) => this.http.get(fullUrl, requestOptions) as Observable<T>
    );
  }

  post<T>(url: string, payload: unknown, options: HttpOptions = {}, apiVersion?: string): Observable<T> {
    return this.send(url, options, apiVersion, this.getPostHeaders(), (fullUrl, requestOptions) =>
      this.http.post<T>(fullUrl, payload, requestOptions)
    );
  }

  patch<T>(url: string, payload: unknown, options: HttpOptions = {}, apiVersion?: string): Observable<T> {
    return this.send(url, options, apiVersion, this.getPostHeaders(), (fullUrl, requestOptions) =>
      this.http.patch<T>(fullUrl, payload, requestOptions)
    );
  }

  put<T>(url: string, payload: unknown, options: HttpOptions = {}, apiVersion?: string): Observable<T> {
    return this.send(url, options, apiVersion, this.getPostHeaders(), (fullUrl, requestOptions) =>
      this.http.put<T>(fullUrl, payload, requestOptions)
    );
  }

  delete<T>(url: string, options: HttpOptions = {}, apiVersion?: string): Observable<T> {
    return this.send(url, options, apiVersion, this.getPostHeaders(), (fullUrl, requestOptions) =>
      this.http.delete<T>(fullUrl, requestOptions)
    );
  }

  private send<T>(
    url: string,
    options: HttpOptions,
    apiVersion: string | undefined,
    extraHeaders: Record<string, string | string[]>,
    request: (fullUrl: string, requestOptions: HttpOptions) => Observable<T>
  ): Observable<T> {
    return this.getToken().pipe(
      switchMap((idToken) => {
        const headers = {
          ...this.getBaseHeaders(idToken),
          ...extraHeaders,
          ...(apiVersion ? { 'Api-Version': apiVersion } : {}),
        };
        options.headers = this.mergeHeaders(options.headers, headers);
        return request(this.apiUrl + url, options);
      })
    );
  }

  private getBaseHeaders(idToken: string): {
    [header: string]: string | string[];
  } {
    if (!idToken) return {};

    if (this.subscriptionKey) {
      return {
        Authorization: `Bearer ${idToken}`,
        'Ocp-Apim-Subscription-Key': this.subscriptionKey,
      };
    }

    return { Authorization: `Bearer ${idToken}` };
  }

  private getPostHeaders(): { [header: string]: string | string[] } {
    return {
      'Content-Type': 'application/json',
      'Ocp-Apim-Trace': 'true',
    };
  }

  public getAccessToken(): string {
    return this._idToken.getValue();
  }

  getToken(): Observable<string> {
    return of(this.store.selectSnapshot(UserState.token));
  }
}
